import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';

export type Density = 'condensed' | 'regular' | 'relaxed';

const VALUES: Density[] = ['condensed', 'regular', 'relaxed'];

/**
 * Owns the stat-table row density (HLTV "offer a density control" lesson).
 * Mirrors ThemeService: the initial value is applied before paint by the inline
 * script in app/index.html (which writes data-density on <html>); this service
 * reads it back, keeps it in sync, and persists to a cookie + localStorage.
 *
 * Density only tunes the --table-row-pad token, so it changes table row padding
 * and nothing else.
 */
export default class DensityService extends Service {
  @tracked density: Density = this.readInitial();

  private readInitial(): Density {
    if (typeof document === 'undefined') return 'regular';
    const attr = document.documentElement.getAttribute('data-density');
    return VALUES.includes(attr as Density) ? (attr as Density) : 'regular';
  }

  get options(): Density[] {
    return VALUES;
  }

  set(density: Density): void {
    this.density = density;
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-density', density);
      document.cookie = `density=${density};path=/;max-age=31536000;samesite=lax`;
      try {
        localStorage.setItem('density', density);
      } catch {
        /* storage unavailable — cookie still holds the choice */
      }
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    density: DensityService;
  }
}
